/**
 * HubLayout — new redesign shell (HUB-REDESIGN P1).
 *
 * Owns: Sidebar (Operate/Platform IA), TopBar (theme toggle), and the
 * outlet that holds the active page. The shell sets the data-theme attribute
 * on its own root so the design tokens in tokens.css scope only to the hub
 * subtree (no leak into the app).
 *
 * P1 deliverable per HUB-REDESIGN: replaces the previous layout (queue-led
 * dev-flavored sidebar, KPI strip, activity rail). Per-page surfaces are
 * filled in P2-P5; existing pages still render under the new shell.
 */
import React, { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  HubThemeProvider, useHubTheme, Sidebar, TopBar, HUB_NAV,
} from './components'
import {
  hubMe, hubStats, clearHubToken, type HubStats,
} from './api'
import './tokens.css'

export default function HubLayout() {
  return (
    <HubThemeProvider>
      <HubLayoutInner />
    </HubThemeProvider>
  )
}

function HubLayoutInner() {
  const { theme } = useHubTheme()
  const [admin, setAdmin] = useState<{ email: string; name: string } | null>(null)
  const [stats, setStats] = useState<HubStats | null>(null)
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    document.title = 'Tenon Hub'
    hubMe().then(setAdmin).catch(() => {})
  }, [])

  useEffect(() => {
    let cancelled = false
    const fetchStats = () => {
      if (document.hidden) return
      hubStats()
        .then((s) => { if (!cancelled) { setStats(s); setRefreshedAt(new Date()) } })
        .catch(() => {})
    }
    fetchStats()
    const id = setInterval(fetchStats, 30_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  const signOut = () => {
    clearHubToken()
    navigate('/auth/login', { replace: true })
  }

  // Sidebar count badges. Customer at-risk count is a P3 follow-up; for
  // now wire what /hub/stats already exposes (errors_today_open + open_p0)
  // to the Engineering pill so the visual signal works end-to-end in P1.
  const counts: Partial<Record<string, { count: number; tone?: 'warn' | 'err' }>> = {}
  if (stats) {
    const engCount = (stats.open_p0 ?? 0) + (stats.errors_today_open ?? 0)
    if (engCount > 0) counts.engineering = { count: engCount, tone: 'err' }
  }

  // Map the active route to a "here" + crumb stack. Uses the first matching
  // NAV item's label; falls back to the URL path for unknown routes.
  const here = pageLabelFor(location.pathname)

  return (
    <div className="tenon-hub" data-theme={theme}>
      <div className="app">
        <Sidebar admin={admin} counts={counts} />
        <main className="main">
          <TopBar here={here} refreshedAt={refreshedAt} onSignOut={signOut} />
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function pageLabelFor(pathname: string): string {
  for (const g of HUB_NAV) {
    for (const i of g.items) {
      if (pathname === i.to || pathname.startsWith(i.to + '/')) return i.label
    }
  }
  // /api-keys is the only orphan surface after P4 (HUB-12-9 super_admin token
  // audit). Everything else either lives in the sidebar or redirects.
  if (pathname.startsWith('/api-keys')) return 'API keys'
  return 'Hub'
}
